import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { LOCATION_CHANGE } from 'connected-react-router';
import api from 'client/services/api';
import {
    selectAddressAutocomplete,
    reset,
    setOptions,
    searchFulfilled,
    searchPending,
    searchRejected,
    autocompleteFulfilled,
    autocompletePending,
    autocompleteRejected,
    setInputValue,
    setSelectedOption,
} from './addressAutocompleteSlice';

const AUTOCOMPLETE_FIELDS = ['city', 'state', 'country', 'zip'];

function triggerFieldChange({ name, value }) {
    const field = document.querySelector(`[name="${name}"]`);
    if (!field) {
        return;
    }

    // we use the browser native protypal set method to set the
    // value to ensure that React can pick it up and propagate
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value',
    )?.set;
    nativeInputValueSetter?.call(field, value);

    // React listens to the 'input' event on text fields and
    // dispatches its own synthetic 'change' event
    // so we dispatch the 'input' DOM event for React to pick up
    const changeEvent = new Event('input', { bubbles: true });
    field.dispatchEvent(changeEvent);
}

function* resetAddressAutocompleteSaga() {
    yield put(reset());
}

function* searchForAddressSuggestionsSaga({ payload: { inputValue } }) {
    yield delay(600);

    const { autocompletedValue, selectedOption } = yield select(
        selectAddressAutocomplete,
    );

    const queryText = inputValue?.trim();
    if (
        !queryText ||
        queryText === autocompletedValue ||
        queryText === selectedOption?.label
    ) {
        yield put(setOptions([]));
        return;
    }

    yield put(searchPending());
    try {
        const results = yield call(
            [api.location, 'searchForAddressSuggestions'],
            { queryText, language: document.documentElement.lang },
        );

        yield put(searchFulfilled(results ? [...results] : []));
    } catch (error) {
        console.error(error);
        yield put(searchRejected(error));
    }
}

function* autocompleteAddressSaga({
    payload: { selectedOption, fieldName, enabledFields },
}) {
    if (!selectedOption?.placeId) {
        return;
    }

    yield put(autocompletePending());
    try {
        const result = yield call(
            [api.location, 'selectAddressForAutocomplete'],
            {
                placeId: selectedOption.placeId,
                language: document.documentElement.lang,
            },
        );

        const autocompletedValue = result.address1;

        yield put(
            autocompleteFulfilled({
                autocompletedAddress: result,
                autocompletedValue: autocompletedValue || null,
            }),
        );

        yield call(autocompleteFieldsSaga, {
            enabledFields,
            fieldName,
            fieldValue: autocompletedValue,
            address: result,
        });
    } catch (error) {
        console.error(error);
        yield put(autocompleteRejected(error));
    }
}

function* autocompleteFieldsSaga({
    enabledFields,
    fieldName,
    fieldValue,
    address,
}) {
    if (fieldValue) {
        yield call(triggerFieldChange, { name: fieldName, value: fieldValue });
    }

    for (const field of AUTOCOMPLETE_FIELDS) {
        if (enabledFields.includes(field) && address[field]) {
            yield call(triggerFieldChange, {
                name: field,
                value: address[field],
            });
        }
    }
}

export default function* () {
    yield takeLatest(LOCATION_CHANGE, resetAddressAutocompleteSaga);
    yield takeLatest(setInputValue.type, searchForAddressSuggestionsSaga);
    yield takeLatest(setSelectedOption.type, autocompleteAddressSaga);
}
